const PANORAMA_SESSION_STORAGE_KEY = 'panorama_session_id_2026'
const PANORAMA_TRACKING_CONTEXT_STORAGE_KEY = 'panorama_tracking_context_2026'
const SURVEY_EDITION = '2026'

export type PanoramaRespondentPath = 'ai_user' | 'non_ai_user'
export type PanoramaStepKey =
  | 'section_a'
  | 'section_b'
  | 'section_c'
  | 'section_d'
  | 'section_e'
  | 'section_f'
  | 'section_g'
  | 'section_h'

export type PanoramaEventName =
  | 'panorama_landing_view'
  | 'panorama_video_play'
  | 'panorama_start_click'
  | 'panorama_step_view'
  | 'panorama_step_ready'
  | 'panorama_path_selected'
  | 'panorama_step_next'
  | 'panorama_step_back'
  | 'panorama_submit_attempt'
  | 'panorama_submit_success'
  | 'panorama_submit_error'
  | 'panorama_abandon'
  | 'panorama_result_view'
  | 'panorama_share_click'
  | 'panorama_outbound_click'

type PanoramaMetadataValue = string | number | boolean | null

export interface PanoramaTrackingContext {
  [key: string]: string | undefined
  session_id: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  referrer_host?: string
  landing_path?: string
}

interface TrackPanoramaEventOptions {
  eventName: PanoramaEventName
  stepKey?: PanoramaStepKey
  stepIndex?: number
  stepCount?: number
  respondentPath?: PanoramaRespondentPath
  metadata?: Record<string, PanoramaMetadataValue | undefined>
  useBeacon?: boolean
}

function readLocalStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeLocalStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Storage can be unavailable in private browsing or strict privacy modes.
  }
}

function generateSessionId(): string {
  if (typeof window.crypto?.randomUUID === 'function') {
    return window.crypto.randomUUID()
  }

  return `panorama-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function getReferrerHost(): string | undefined {
  if (!document.referrer) return undefined

  try {
    return new URL(document.referrer).host || undefined
  } catch {
    return undefined
  }
}

function sanitizeContextValue(value?: string | null): string | undefined {
  const normalized = value?.trim()
  return normalized ? normalized : undefined
}

function sanitizeMetadata(
  metadata?: Record<string, PanoramaMetadataValue | undefined>
): Record<string, PanoramaMetadataValue> {
  if (!metadata) return {}

  return Object.entries(metadata).reduce<Record<string, PanoramaMetadataValue>>((acc, [key, value]) => {
    if (value !== undefined) acc[key] = value
    return acc
  }, {})
}

function panoramaBasePath(): string {
  const match = window.location.pathname.match(/^(.*\/panorama-ecommerce-2026)(?:\/[^/]+)?$/)
  return match?.[1] || '/panorama-ecommerce-2026'
}

function getPrimitiveMetadata(
  metadata: Record<string, PanoramaMetadataValue>
): Record<string, PanoramaMetadataValue> {
  return Object.entries(metadata).reduce<Record<string, PanoramaMetadataValue>>((acc, [key, value]) => {
    if (value === null || ['string', 'number', 'boolean'].includes(typeof value)) {
      acc[key] = value
    }
    return acc
  }, {})
}

export function getPanoramaTrackingContext(): PanoramaTrackingContext {
  const existing = readLocalStorage(PANORAMA_TRACKING_CONTEXT_STORAGE_KEY)
  let stored: Partial<PanoramaTrackingContext> = {}

  if (existing) {
    try {
      stored = JSON.parse(existing) as Partial<PanoramaTrackingContext>
    } catch {
      stored = {}
    }
  }

  const search = new URLSearchParams(window.location.search)
  const sessionId = sanitizeContextValue(stored.session_id) || generateSessionId()
  const context: PanoramaTrackingContext = {
    session_id: sessionId,
    utm_source: sanitizeContextValue(search.get('utm_source')) || sanitizeContextValue(stored.utm_source),
    utm_medium: sanitizeContextValue(search.get('utm_medium')) || sanitizeContextValue(stored.utm_medium),
    utm_campaign: sanitizeContextValue(search.get('utm_campaign')) || sanitizeContextValue(stored.utm_campaign),
    utm_content: sanitizeContextValue(search.get('utm_content')) || sanitizeContextValue(stored.utm_content),
    utm_term: sanitizeContextValue(search.get('utm_term')) || sanitizeContextValue(stored.utm_term),
    referrer_host: getReferrerHost() || sanitizeContextValue(stored.referrer_host),
    landing_path: sanitizeContextValue(stored.landing_path) || window.location.pathname,
  }

  writeLocalStorage(PANORAMA_SESSION_STORAGE_KEY, sessionId)
  writeLocalStorage(PANORAMA_TRACKING_CONTEXT_STORAGE_KEY, JSON.stringify(context))

  return context
}

export function buildPanoramaResponseTrackingContext(): PanoramaTrackingContext {
  return getPanoramaTrackingContext()
}

function dispatchFirstPartyEvent(payload: object, useBeacon = false): void {
  const body = JSON.stringify(payload)
  const endpoint = `${panoramaBasePath()}/events`

  if (useBeacon && typeof navigator.sendBeacon === 'function') {
    const blob = new Blob([body], { type: 'application/json' })
    navigator.sendBeacon(endpoint, blob)
    return
  }

  void fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body,
    keepalive: useBeacon,
  })
}

export function trackPanoramaEvent({
  eventName,
  stepKey,
  stepIndex,
  stepCount,
  respondentPath,
  metadata,
  useBeacon = false,
}: TrackPanoramaEventOptions): void {
  const context = getPanoramaTrackingContext()
  const sanitizedMetadata = sanitizeMetadata(metadata)

  dispatchFirstPartyEvent({
    panorama_survey_event: {
      survey_edition: SURVEY_EDITION,
      session_id: context.session_id,
      event_name: eventName,
      step_key: stepKey,
      step_index: stepIndex,
      step_count: stepCount,
      respondent_path: respondentPath,
      utm_source: context.utm_source,
      utm_medium: context.utm_medium,
      utm_campaign: context.utm_campaign,
      utm_content: context.utm_content,
      utm_term: context.utm_term,
      referrer_host: context.referrer_host,
      page_path: window.location.pathname,
      metadata: sanitizedMetadata,
    },
  }, useBeacon)

  window.gtag?.('event', eventName, {
    survey_edition: SURVEY_EDITION,
    session_id: context.session_id,
    step_key: stepKey,
    step_index: stepIndex,
    step_count: stepCount,
    respondent_path: respondentPath,
    page_path: window.location.pathname,
    ...getPrimitiveMetadata(sanitizedMetadata),
  })
}
